import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { assetKeys } from './assets.query';

const unwrap = (raw: any) => (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw ?? {});
const BASE = '/api/admin/assets';

export type AssetHistory = {
  asid: number;
  changes: { at: string | null; changes: Record<string, [unknown, unknown]> }[];
  admin_actions: {
    at: string | null;
    action: string;
    actor: string | number | null;
    meta: Record<string, unknown> | null;
    changes: Record<string, { from: unknown; to: unknown }> | null;
    outcome: string | null;
  }[];
};

export const EDITABLE_FLAGS = [
  ['featured', 'Featured'], ['logo', 'Logo'], ['floor_plan', 'Floor plan'], ['site_plan', 'Site plan'],
  ['is_360', '360°'], ['slider', 'Slider'], ['advert', 'Advert'], ['thumbnail', 'Thumbnail'], ['tester', 'Test data'],
] as const;

/** Every write here changes the library and drawer data: refresh both. */
const useAssetWrite = <P,>(build: (p: P) => { url: string; method?: string; body?: unknown }, msg: string) => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: withErrorHandling(async (p: P) => {
      const { url, method = 'POST', body } = build(p);
      return unwrap(await request(url, method as any, body as any, { timeout: 120000 }));
    }, msg),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: assetKeys.all });
      qc.invalidateQueries({ queryKey: ['asset-history'] });
    },
  });
};

export const usePatchAssetMutation = () =>
  useAssetWrite<{ asid: number; fields: Record<string, unknown>; reason?: string }>(
    ({ asid, fields, reason }) => ({ url: `${BASE}/${asid}`, method: 'PATCH', body: { fields, reason } }), 'Could not save');

export const usePrivacyMutation = () =>
  useAssetWrite<{ asid: number; private: boolean; reason: string }>(
    ({ asid, ...b }) => ({ url: `${BASE}/${asid}/privacy`, body: b }), 'Could not change privacy');

export const useMakeCoverMutation = () =>
  useAssetWrite<{ asid: number }>(({ asid }) => ({ url: `${BASE}/${asid}/cover` }), 'Could not make it the cover');

export const useMoveMutation = () =>
  useAssetWrite<{ asid: number; eid: number; reason?: string; dry_run?: boolean }>(
    ({ asid, ...b }) => ({ url: `${BASE}/${asid}/move`, body: b }), 'Could not move the photo');

export const usePurgeMutation = () =>
  useAssetWrite<{ asid: number; dry_run?: boolean; reason?: string; confirm_asid?: number }>(
    ({ asid, ...b }) => ({ url: `${BASE}/${asid}/purge`, body: b }), 'Could not purge');

export const useReplaceMutation = () =>
  useAssetWrite<{ asid: number; file: File; reason: string }>(({ asid, file, reason }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('reason', reason);
    return { url: `${BASE}/${asid}/replace`, body: fd };
  }, 'Could not replace the photo');

export const useUploadForSellerMutation = () =>
  useAssetWrite<{ eid: number; files: File[]; reason: string; cover: boolean }>(({ eid, files, reason, cover }) => {
    const fd = new FormData();
    fd.append('eid', String(eid));
    fd.append('reason', reason);
    fd.append('cover', cover ? '1' : '0');
    files.forEach((f) => fd.append('files', f));
    return { url: `${BASE}/upload`, body: fd };
  }, 'Upload failed');

export const useAssetHistoryQuery = (asid: number | undefined, enabled: boolean) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: ['asset-history', asid],
    queryFn: withErrorHandling(async (): Promise<AssetHistory> => unwrap(await request(`${BASE}/${asid}/history`)), 'Could not load history'),
    enabled: enabled && asid != null,
  });
};

/** Download the Library's current filter as CSV (auth travels with the request). */
export const useExportCsv = () => {
  const request = useClientStore((s) => s.request);
  return async (params: Record<string, unknown>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (!['page', 'page_size', 'facets'].includes(k) && v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    });
    const raw: any = await request(`${BASE}/export?${qs}`, 'GET', undefined, { timeout: 180000 });
    const text = typeof raw === 'string' ? raw : typeof raw?.data === 'string' ? raw.data : '';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    a.download = `assets-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    return text.split('\n').length - 2;
  };
};
